import {IItem} from "../../interfaces/Item";
import {BARCODE, PICTURE, VARIABLE_PICTURE, SIMPLE_TEXT, VARIABLE_TEXT, HORIZONTAL_LINE, VERTICAL_LINE} from "../../utils/ItemTypes";
import {useAppDispatch} from "../../app/hooks";
import {AddItemToLabel, ChangeBarcodeResize, ChangeLineValue, ChangeObjectResize, ChangeValue, DeleteSelectedItem, SelectItem, SetCoordinates} from "../MainActions";
import Moveable, {MoveableManagerInterface, Renderer} from "react-moveable";
import {useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {RootState} from "../../app/store";
import {GenerateBarcode} from "../BarcodeActions";
import {C128, DATAMATRIX, DATAMATRIX_GS1, QR} from "../../utils/barcodeUtils";
import {IBarcode} from "../../interfaces/Barcode";
import defaultImg from "../../image.png";
import {convertPointsToPixels} from "../../utils/constants";
import {ActionCreators as UndoActionCreators} from "redux-undo";

interface MoveableObjectProps {
  item: IItem;
  zoom: number;
  setZoom: (zoom: number) => void;
}

export const MoveableObject = ({item, zoom, setZoom}: MoveableObjectProps) => {
  const dispatch = useAppDispatch();
  const targetRef = useRef<HTMLDivElement | null>(null);
  const [editInput, setEditInput] = useState(false);
  const [localTextValue, setLocalTextValue] = useState(item.value);
  const textDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const {pxInMM, dpi, showDefaultValues, showGrid, gridCellWidth} = useSelector((state: RootState) => state.labelEdit.present.labelParams);
  const {labelItems} = useSelector((state: RootState) => state.labelEdit.present);

  useEffect(() => {
    if (item.value !== localTextValue && !editInput) {
      setLocalTextValue(item.value);
    }
  }, [item.value, editInput]);

  const calculateRestrictedRotation = (angle: number) => {
    const angles = [0, 90, 180, 270, 360];
    const closest = angles.reduce((prev, curr) => {
      return Math.abs(curr - angle) < Math.abs(prev - angle) ? curr : prev; //find the closest angle
    });
    return closest === 360 ? 0 : closest;
  };

  const onBarcodeScaleChange = (width?: number, height?: number) => {
    setZoom(0);
    const barcode: IBarcode = {
      X: item?.xInMM || 0,
      Y: item?.yInMM || 0,
      Name: item?.name || "",
      Width: width || 0,
      Height: height || 0,
      ShowCaption: Number(item?.showCaption) || 0,
      CaptionSize: item?.captionSize || 0,
      Rotation: item?.rotation || 0,
      Type: item?.barcodeType || C128,
      DefaultText: showDefaultValues ? item?.defaultText : "",
      DataMatrixSize: item?.dataMatrixSize || "Auto",
    };
    dispatch(GenerateBarcode(item.itemid, barcode)).then((barcodeUrl: any) => {
      if (barcodeUrl && item !== undefined && item !== null) {
        dispatch(ChangeBarcodeResize(item, width || 0, height || 0, barcodeUrl));
      }
    });
  };

  const [copiedItem, setCopiedItem] = useState<IItem>();

  useEffect(() => {
    // console.log('[MoveableObject] Registering keyboard handlers - editInput:', editInput);
    const generateNewId = () => {
      return Math.floor(Number((labelItems.length + 1).toString() + (Math.random() * 100000000).toString()));
    };
    const handleKeyDown = (event: any) => {
      const code = event.which || event.keyCode;

      let charCode = String.fromCharCode(code).toLowerCase();
      if ((event.ctrlKey || event.metaKey) && charCode === "c" && !editInput) {
        if (event.target.tagName.toLowerCase() !== "input" && event.target.tagName.toLowerCase() !== "textarea") {
          event.preventDefault();
          const selectedItem = labelItems.find((item: IItem) => item.selected);
          setCopiedItem(selectedItem);
        }
      } else if ((event.ctrlKey || event.metaKey) && charCode === "v" && !editInput) {
        if (event.target.tagName.toLowerCase() !== "input" && event.target.tagName.toLowerCase() !== "textarea") {
          event.preventDefault();
          if (copiedItem) {
            setZoom(0);
            if (copiedItem.type !== HORIZONTAL_LINE && copiedItem.type !== VERTICAL_LINE) {
              const newItem: IItem = {
                ...copiedItem,
                itemid: generateNewId(),
                selected: false,
                xInMM: copiedItem.xInMM + 5,
                yInMM: copiedItem.yInMM + 5,
                style: {
                  ...copiedItem.style,
                  top: `${(copiedItem.yInMM + 5) * pxInMM}px`,
                  left: `${(copiedItem.xInMM + 5) * pxInMM}px`,
                },
              };
              dispatch(AddItemToLabel(newItem));
            } else {
              const newItem: IItem = {
                ...copiedItem,
                itemid: generateNewId(),
                selected: false,
                xInMM: copiedItem.xInMM + 5 || 5,
                yInMM: copiedItem.yInMM + 5 || 5,
                xToInMM: copiedItem.xToInMM ? copiedItem.xToInMM + 5 : 5,
                yToInMM: copiedItem.yToInMM ? copiedItem.yToInMM + 5 : 5,
                style: {
                  ...copiedItem.style,
                  top: `${(copiedItem.yInMM + 5) * pxInMM}px`,
                  left: `${(copiedItem.xInMM + 5) * pxInMM}px`,
                },
              };
              dispatch(AddItemToLabel(newItem));
            }
          }
        }
      } else if ((event.ctrlKey || event.metaKey) && charCode === "z" && !event.shiftKey) {
        event.preventDefault();
        setZoom(0);
        dispatch(UndoActionCreators.undo());
      } else if ((event.ctrlKey || event.metaKey) && charCode === "z" && event.shiftKey) {
        event.preventDefault();
        setZoom(0);
        dispatch(UndoActionCreators.redo());
      } else if ((event.key === "Backspace" || event.key === "Delete") && !editInput) {
        if (event.target.tagName.toLowerCase() !== "input" && event.target.tagName.toLowerCase() !== "textarea") {
          event.preventDefault();
          setZoom(0);
          dispatch(DeleteSelectedItem());
        }
      } else {
        return;
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      // console.log('[MoveableObject] Cleanup keyboard handlers');
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [copiedItem, dispatch, editInput, labelItems.length, pxInMM, setZoom]);

  const renderItem = (item: IItem) => {
    switch (item.type) {
      case SIMPLE_TEXT:
        return (
          <textarea
            style={{
              fontSize: `${convertPointsToPixels(item.fontSize || 10, dpi)}px`,
              width: (item.width || 0) * pxInMM,
              fontFamily: item.fontName,
              fontWeight: item.fontBold ? "bold" : "normal",
              display: "block",
              resize: "none",
              overflow: "hidden",
              padding: 0,
              border: "none",
              margin: 0,
              boxSizing: "border-box",
              backgroundColor: "transparent",
            }}
            rows={item.value.split("\n").length}
            value={localTextValue}
            onChange={(e) => {
              if(!editInput) return;
              setZoom(0);
              setLocalTextValue(e.target.value);
              if (textDebounceTimerRef.current) {
                clearTimeout(textDebounceTimerRef.current);
              }
              textDebounceTimerRef.current = setTimeout(() => {
                dispatch(ChangeValue(item, "value", e.target.value));
              }, 300);
            }}
            readOnly={!item.selected && !editInput}
            onBlur={() => {
              targetRef.current?.blur();
              setZoom(0);
              setEditInput(false);
              if (textDebounceTimerRef.current) {
                clearTimeout(textDebounceTimerRef.current);
              }
              if (localTextValue !== item.value) {
                dispatch(ChangeValue(item, "value", localTextValue));
              }
            }}
            onFocus={(e) => {
              e.stopPropagation();
              dispatch(SelectItem(item));
              // setZoom(1);
              setEditInput(true);
            }}
          />
        );
      case VARIABLE_TEXT:
        return (
          <p
            style={{
              fontSize: `${convertPointsToPixels(item.fontSize || 10, dpi)}px`,
              width: (Number(item.width) || 0) * pxInMM,
              // border: "1px solid black",
              fontFamily: item.fontName,
              fontWeight: item.fontBold ? "bold" : "normal",
              textWrap: item.wrap ? "wrap" : "nowrap",
              overflow: "hidden",
              // height: `${convertPointsToPixels(item.fontSize || 10, dpi)}px`,
              boxSizing: "border-box",
              textAlign: item.align as CanvasTextAlign,
              whiteSpace: "pre-wrap",
            }}
          >
            {showDefaultValues && item.defaultText !== "" ? item.defaultText : item.name}
          </p>
        );
      case PICTURE:
      case VARIABLE_PICTURE:
        return (
          <img
            src={item.value !== "" ? item.value : defaultImg}
            alt={item.type === PICTURE ? "simple picture" : "variable picture"}
            style={{
              width: (item.width || 0) * pxInMM,
              height: (item.height || 0) * pxInMM,
              display: "block",
            }}
          />
        );
      case HORIZONTAL_LINE:
        return (
          <hr
            style={{
              width: (item.width || 0) * pxInMM,
              height: (item.lineWidth || 0) * pxInMM,
              border: "none",
              margin: 0,
              padding: 0,
              borderTop: `${(item.lineWidth || 0) * pxInMM}px solid black`,
            }}
          />
        );
      case VERTICAL_LINE:
        return (
          <hr
            style={{
              width: (item.lineWidth || 0) * pxInMM,
              height: (item.width || 0) * pxInMM,
              border: "none",
              margin: 0,
              padding: 0,
              borderLeft: `${(item.lineWidth || 0) * pxInMM}px solid black`,
            }}
          />
        );
      case BARCODE:
        return (
          <img
            style={{
              width: (item.width || 0) * pxInMM,
              height: (item.height || 0) * pxInMM,
              margin: 0, 
              boxSizing: "border-box",
              objectFit: "contain",
              objectPosition: "left"
            }}
            src={item.value}
            alt="barcode"
          />
        );
      default:
        return null;
    }
  };

  const PositionViewable = {
    name: "positionViewable",
    props: ["positionViewable", "dragControlCondition"],
    events: [],
    render(moveable: MoveableManagerInterface<any, any>, React: Renderer) {
      const rect = moveable.getRect();
      return (
        <div
          key={"position-viewer"}
          className={"moveable-position"}
          style={{
            position: "absolute",
            left: `${rect.width / 2}px`,
            top: `${rect.height + 7}px`,
            background: "#4af",
            borderRadius: "1px",
            padding: "1px 4px",
            color: "white",
            fontSize: "12.5px",
            whiteSpace: "nowrap",
            // fontWeight: "bold",
            willChange: "transform",
            transform: `translate(-50%, 0px)`,
          }}
        >
          {Math.round(rect.left / pxInMM)}, {Math.round(rect.top / pxInMM)}
        </div>
      );
    },
  } as const;

  const [showPosition, setShowPosition] = useState(false);

  return (
    <div
      className="moveable-item"
      key={item.itemid}
      onClick={() => {
        //targetRef.current?.focus();
        dispatch(SelectItem(item));
        setZoom(1);
      }}
    >
      <div
        style={{
          ...item.style,
          maxWidth: item.type !== VERTICAL_LINE ? "auto" : (item.lineWidth || 0) * pxInMM + "px",
          minWidth: "auto",
          minHeight: "auto",
          maxHeight: item.type !== HORIZONTAL_LINE ? "auto" : (item.lineWidth || 0) * pxInMM + "px",
          zIndex: item.bringBack || 2,
        }}
        ref={targetRef}
      >
        {renderItem(item)}
      </div>
      <Moveable
        target={targetRef}
        draggable={item.selected}
        throttleDrag={1}
        edgeDraggable={item.selected}
        startDragRotate={0}
        throttleDragRotate={0}
        checkInput={editInput}
        padding={0}
        ables={[PositionViewable]}
        props={{positionViewable: item.selected && zoom === 1 && showPosition, dragControlCondition: {left: true, top: true, right: true, bottom: true, rotation: true}}}
        hideDefaultLines={!item.selected}
        renderDirections={
          item.type === VERTICAL_LINE ? ["n", "s"] :
          (item.type === HORIZONTAL_LINE || item.type === SIMPLE_TEXT || item.type === VARIABLE_TEXT) ? ["w","e"] : 
          ["nw", "n", "ne", "sw", "s", "se"]
        }        
        resizable={item.selected}
        throttleResize={1}
        rotatable={item.selected && item.type !== BARCODE && item.type !== HORIZONTAL_LINE && item.type !== VERTICAL_LINE}
        throttleRotate={0}
        snappable={showGrid}
        snapGridWidth={pxInMM * (gridCellWidth ? gridCellWidth : 10) * 0.5}
        snapGridHeight={pxInMM * (gridCellWidth ? gridCellWidth : 10) * 0.5}
        isDisplayGridGuidelines={true}
        rotationPosition={"top"}
        updateRect={"Start"}
        origin={false}
        keepRatio={item.keepRatio || (item.type === BARCODE && (item.barcodeType === QR || item.barcodeType === DATAMATRIX || item.barcodeType === DATAMATRIX_GS1))}
        zoom={zoom} //hide control box
        onClick={(e: any) => {
          setZoom(1);
          if (e.isDouble && item.type === SIMPLE_TEXT && !editInput) {
            setEditInput(true);
          }
        }}
        onDragStart={() => setZoom(1)}
        onDrag={(e: any) => {
          setShowPosition(true);
          e.target.style.left = `${e.left}px`;
          e.target.style.top = `${e.top}px`;
        }}
        onDragEnd={(e: any) => {
          setShowPosition(false);
          if (!e.isDrag || !e.lastEvent) return;
          if (item.type === HORIZONTAL_LINE) {
            dispatch(ChangeLineValue(item, e.lastEvent.left / pxInMM, e.lastEvent.top / pxInMM, (e.lastEvent.left + (item.width || 0) * pxInMM) / pxInMM, e.lastEvent.top / pxInMM));
          } else if (item.type === VERTICAL_LINE) {
            dispatch(ChangeLineValue(item, e.lastEvent.left / pxInMM, e.lastEvent.top / pxInMM, e.lastEvent.left / pxInMM, (e.lastEvent.top + (item.width || 0) * pxInMM) / pxInMM));
          } else {
            dispatch(SetCoordinates(item, e.lastEvent.left, e.lastEvent.top));
          }
        }}
        onResize={(e) => {
          e.target.style.width = `${e.width}px`;
          e.target.style.height = `${e.height}px`;
          e.target.style.left = `${e.drag.left}px`;
          e.target.style.top = `${e.drag.top}px`;
        }}
        onResizeEnd={(e: any) => {
          if (!e.lastEvent) return;
          const left = e.lastEvent.drag.left;
          const top = e.lastEvent.drag.top;
          const xInMM = left / pxInMM || 0;
          const yInMM = top / pxInMM || 0;
          const width = e.lastEvent.width / pxInMM;
          const height = e.lastEvent.height / pxInMM;

          if (item.type === VERTICAL_LINE) {
            dispatch(ChangeObjectResize(item, height, "yToInMM", height + yInMM));
            dispatch(ChangeLineValue(item, xInMM, yInMM, xInMM, height + yInMM));
          } else if (item.type === HORIZONTAL_LINE) {
            dispatch(ChangeObjectResize(item, width, "xToInMM",  width + xInMM));
            dispatch(ChangeLineValue(item, xInMM, yInMM, width + xInMM, yInMM));
          } else if (item.type === BARCODE) {
            onBarcodeScaleChange(width, height);
            dispatch(SetCoordinates(item, left, top, true));
          } else {
            dispatch(ChangeObjectResize(item, width, "height", height));
            dispatch(SetCoordinates(item, left, top, true));
          }
        }}
        onRotateStart={(e: any) => {
          setZoom(1);
        }}
        onRotate={(e: any) => {
          if (item.type !== HORIZONTAL_LINE && item.type !== VERTICAL_LINE) {
            const restrictedAngle = calculateRestrictedRotation(e.rotation);
            e.target.style.transform = `rotate(${restrictedAngle}deg)`;
          }
        }}
        onRotateEnd={(e: any) => {
          if (item.type !== HORIZONTAL_LINE && item.type !== VERTICAL_LINE) {
            const restrictedAngle = calculateRestrictedRotation(e.lastEvent.rotation);
            dispatch(ChangeValue(item, "rotation", restrictedAngle));
          }
        }}
      />
    </div>
  );
};
